import type { CurrencyCode, UnknownEvent } from '@faststore/sdk'
import { sendAnalyticsEvent, useSession } from '@faststore/sdk'
import axios from 'axios'
import { useCallback, useState } from 'react'
import { useCart } from 'src/sdk/cart/useCart'
import type { CartItem } from 'src/sdk/cart/validate'
import { getCartFromOrderForm, validateCart } from 'src/sdk/cart/validate'

import { usePageTypeContext } from 'src/contexts/page-type-context'
import {
  useAuthenticatedUserToken,
  useUserToken,
} from '../analytics/hooks/useUserToken'
import { useApplicationInsightsAPI } from '../insights/useApplicationInsightsAPI'
import { buildCustomEvent } from '../insights/utils'
import { updateCartItem } from './handleCartItems'

export const useUpdateQuantity = (
  item: CartItem | null,
  setIsVTEXValidating: (isValidating: boolean) => void
) => {
  const userToken = useUserToken()
  const authenticatedUserToken = useAuthenticatedUserToken()
  const {
    updateItemQuantity: defaultUpdateQuantity,
    id: cartId,
    setCart: setLocaleCart,
    items,
  } = useCart()
  const {
    currency: { code },
    person,
  } = useSession()

  const { trackCustomEvent } = useApplicationInsightsAPI()
  const { pageType } = usePageTypeContext()

  const validateAfterUpdate = async (newCartId: string) => {
    const { data: savedOrderForm } = await axios.get(
      `/api/checkout/getCartInformation?orderFormId=${newCartId}`
    )

    const savedCart = getCartFromOrderForm(savedOrderForm)

    const validatedCart = await validateCart(savedCart)

    if (validatedCart) setLocaleCart(validatedCart)
  }

  const [error, setError] = useState<string | undefined>(undefined)

  const updateItemQuantity = useCallback(
    (updatedQuantity: number) => {
      if (!item) {
        return
      }

      // Quantity unchanged: no event and nothing to update
      const quantityDelta = updatedQuantity - item.quantity

      if (quantityDelta === 0) {
        return
      }

      setIsVTEXValidating(true)

      const itemCategory = item.itemOffered.breadcrumbList?.itemListElement
        .slice(0, -1)
        .map((x) => x.name)

      const itemListName = item.itemOffered.itemListName

      try {
        trackCustomEvent(
          buildCustomEvent(
            item,
            person?.email,
            cartId,
            pageType,
            'minicart-change-quantity-button'
          ),
          item.quantity > updatedQuantity ? 'remove-from-cart' : 'add-to-cart'
        )
      } catch (er) {
        console.log(er)
      }

      const isKit = item.isKit

      const unitMultiplier =
        item.itemOffered.productSpecifications?.unitMultiplier ?? 1

      const analyticsEvent: UnknownEvent = {
        name: quantityDelta < 0 ? 'remove_from_cart' : 'add_to_cart',
        params: {
          orderFormId: cartId,
          // Dynamic Yield tracks only the units added by this change (+1 sends
          // the price of a single pack), never the whole cart line
          ...(quantityDelta > 0 && {
            dynamicYield: {
              currency: code as CurrencyCode,
              value: item.price * quantityDelta,
              quantity: quantityDelta,
              // The price received here already includes the unit multiplier
              priceIncludesUnitMultiplier: true,
              cartItems: items,
            },
          }),
          // Removal: only the units removed by this change, cart as it will be
          ...(quantityDelta < 0 && {
            dynamicYield: {
              currency: code as CurrencyCode,
              value: item.price * -quantityDelta,
              quantity: -quantityDelta,
              cartItems: items
                .map((cartItem) =>
                  cartItem.id === item.id
                    ? { ...cartItem, quantity: updatedQuantity }
                    : cartItem
                )
                .filter(({ quantity }) => quantity > 0),
            },
          }),
          // Adding optional kit parameters
          ...(isKit && {
            bundle_label: item.itemOffered.isVariantOf.name,
            bundle_discount: +(item.listPrice - item.price).toFixed(2),
            bundle_discount_percentage: +(
              1 -
              item.price / item.listPrice
            ).toFixed(2),
          }),
          algolia_user_token: userToken,
          ...(authenticatedUserToken && {
            algolia_authenticated_user_token: authenticatedUserToken,
          }),
          queryID: item.itemOffered.queryID,
          items: [
            {
              object_id: item.itemOffered.sku,
              index: 0,
              item_list_id: null,
              item_category: itemCategory,
              ...(quantityDelta > 0 &&
                itemListName && { item_list_name: itemListName }),
              item_id: item.itemOffered.isVariantOf.productGroupID,
              item_name: item.itemOffered.isVariantOf.name,
              item_brand: item.itemOffered.brand.name,
              item_variant: item.itemOffered.sku,
              quantity: 1,
              unitMultiplier,
              price: item.price,
              discount: +(item.listPrice - item.price).toFixed(2),
              full_price: item.listPrice,
              discount_percentage: +(1 - item.price / item.listPrice).toFixed(
                2
              ),
              currency: code as CurrencyCode,
              item_variant_name: item.itemOffered.name,
              product_reference_id: Number(item.itemOffered.gtin),
              coupon: null,
              // Adding optional kit parameters
              ...(isKit && {
                bundle_label: item.itemOffered.isVariantOf.name,
                bundle_discount: +(item.listPrice - item.price).toFixed(2),
                bundle_discount_percentage: +(
                  1 -
                  item.price / item.listPrice
                ).toFixed(2),
              }),
            },
          ],
        },
      }

      updateCartItem({ ...item, quantity: updatedQuantity }, cartId)
        .then(() => {
          // Sent only once the cart update succeeded
          sendAnalyticsEvent<UnknownEvent>(analyticsEvent)
          defaultUpdateQuantity(item.id, updatedQuantity)
          setIsVTEXValidating(false)
        })
        .catch(() => {
          validateAfterUpdate(cartId)
          setIsVTEXValidating(false)
          setError('Questo prodotto non è più presente nel carrello.')
          setTimeout(() => {
            setError(undefined)
          }, 2000)
        })
    },
    [
      code,
      item,
      items,
      defaultUpdateQuantity,
      cartId,
      setIsVTEXValidating,
      setLocaleCart,
    ]
  )

  return { updateItemQuantity, error }
}
